import React from 'react'
import styled, { css } from 'styled-components'
import { CardRow } from '../../../../../../generic/ui/CardRow'
import { Mono } from '../../../../../../generic/ui/Mono'
import KbdChipButton from '../../../../../../generic/ui/KbdChipButton/KbdChipButton'
import PencilIcon from '../../../../../../generic/icons/IconLib/PencilIcon'
import XIcon from '../../../../../../generic/icons/IconLib/XIcon'
import PackageBoxIcon from '../../../../../../generic/icons/IconLib/PackageBoxIcon'
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon'
import UserIcon from '../../../../../../generic/icons/IconLib/UserIcon'
import ProductImage from '../../../queries/TeudotDetails/Components/ProductImage'
import { ISrikaItem } from '../../../../../../modules/API/Gathering/models/srika'

type Props = {
  item: ISrikaItem
  isLast?: boolean
  onEdit?: () => void
  onClear?: () => void
}

const ProductLocationRow = ({ item, isLast = false, onEdit, onClear }: Props) => {
  const {
    ProductDesc,
    VarietyDesc,
    SizeDesc,
    GrowerName,
    GeneralProductID,
    BalanceWeight,
    Gross,
    BalanceAmount,
    AmountPack,
  } = item
  const productName = `${ProductDesc?.trim() ?? ''} ${VarietyDesc?.trim() ?? ''} ${SizeDesc?.trim() ?? ''}`.trim()
  const hasActions = !!onEdit || !!onClear

  return (
    <CardRow
      $columns={`${hasActions ? '4rem' : '2.75rem'} minmax(0, 1fr) auto`}
      $mobileColumns={`${hasActions ? '3.5rem' : '2.5rem'} minmax(0, 1fr) auto`}
      $gap={hasActions ? '0.5rem 1.25rem' : undefined}
      // Слева (в RTL — сторона чисел и кнопок) на мобиле чуть больше воздуха
      $mobilePadding={hasActions ? '0.625rem 0.5rem 0.625rem 0.875rem' : undefined}
      $isLast={isLast}
      $hover={false}
    >
      <ImageCell $align={hasActions ? 'stretch' : 'center'} $tall={hasActions}>
        <ImageSlot $tall={hasActions}>
          <ProductImage id={GeneralProductID || 0} />
        </ImageSlot>
      </ImageCell>

      <CardRow.Cell>
        <CardRow.Stack $gap='0.25rem'>
          <Name>{productName}</Name>
          {GrowerName && (
            <MetaItem>
              <MetaIcon><UserIcon /></MetaIcon>
              <MetaText>{GrowerName.trim()}</MetaText>
            </MetaItem>
          )}
        </CardRow.Stack>
      </CardRow.Cell>

      <CardRow.Cell $align='center'>
        <CardRow.Stack $gap='0.2rem' $align='end'>
          <Metric>
            <MetaIcon><ScaleIcon /></MetaIcon>
            <Mono>{BalanceWeight?.toFixed(2) || '0'} / {Gross?.toFixed(2) || '0'}</Mono>
          </Metric>
          <Metric>
            <MetaIcon><PackageBoxIcon /></MetaIcon>
            <Mono>{BalanceAmount || 0} / {AmountPack || 0}</Mono>
          </Metric>
        </CardRow.Stack>
      </CardRow.Cell>

      {hasActions && (
        <Actions>
          {onEdit && (
            <ActionButton onClick={onEdit} aria-label='עריכת יתרה' title='עריכת יתרה'>
              <PencilIcon />
            </ActionButton>
          )}
          {onClear && (
            <ActionButton onClick={onClear} aria-label='ניקוי' title='ניקוי'>
              <XIcon />
            </ActionButton>
          )}
        </Actions>
      )}
    </CardRow>
  )
}

export default ProductLocationRow

// С кнопками картинка тянется на обе строки: данные + строка кнопок
const ImageCell = styled(CardRow.Cell)<{ $tall: boolean }>`
  ${({ $tall }) => $tall && css`
    grid-row: span 2;
    align-self: stretch;
  `}
`

// Фиксированный размер только без $tall: stylis выносит @media после объявлений,
// и мобильный размер перебил бы растяжку
const ImageSlot = styled.span<{ $tall: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: ${({ theme }) => theme.radii.sm};
  }

  ${({ $tall }) => $tall ? css`
    flex: 1;
    width: 100%;
    min-height: 0;
  ` : css`
    width: 2.5rem;
    height: 2.5rem;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      width: 2.25rem;
      height: 2.25rem;
    }
  `}
`

const Name = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.lg};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.md};
  }
`

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.ink3};
`

const MetaIcon = styled.span`
  color: ${({ theme }) => theme.colors.icon};
  display: inline-flex;
  align-items: center;

  svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`

const MetaText = styled.span`
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.sm};
  color: ${({ theme }) => theme.colors.ink3};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.sm};
  }
`

// Пары «остаток / всего» — числа слева направо и в RTL
const Metric = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.text.md};
  color: ${({ theme }) => theme.colors.ink};
  white-space: nowrap;

  ${Mono} {
    direction: ltr;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.md};
  }
`

// Отдельная строка под данными, кнопки прижаты к концу (в RTL — влево)
const Actions = styled.div`
  grid-column: 2 / -1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.375rem;
`

const ActionButton = styled(KbdChipButton)`
  && {
    height: 1.875rem;
    min-width: 1.875rem;
    padding: 0 0.375rem;
  }

  && svg {
    width: 0.875rem;
    height: 0.875rem;
  }
`
